import moment from "moment";
import labels from "../../../constants/displayFields";
import useLabels from "../../../hooks/useLabels";
import useFilteredBillingTypes, {
  getProjectStorageBillingTypeOptions,
} from "../../../hooks/useFilteredBillingTypes";

const serverFormat = "YYYY-MM-DDTHH:mm:ss Z";

const formatDate = dateTime => {
  return moment(dateTime, serverFormat).format("MM/DD/YYYY");
};

const section = "identifiers";

export default function useEnabledIdentifiers({
  display,
  disabledForm,
  asset,
  type,
  project,
  isReceivingWizardDisabled,
}) {
  const l = useLabels();
  const projectBillingTypeOptions = project
    ? getProjectStorageBillingTypeOptions(project?.billingTypes || asset?.project?.billingTypes)
    : null;
  const filteredBillingTypeOptions = useFilteredBillingTypes(projectBillingTypeOptions);
  const isItem = type === "item";

  const fields = [
    {
      id: "item_id",
      enabled: display.item_id,
      type: "text_field",
      section,
      params: {
        label: labels.item_id,
        disabled: true,
        value: asset.itemId,
        fullWidth: true,
        size: "small",
      },
    },
    {
      id: "barcode_number",
      enabled: display.barcode_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "barcode",
        label: l.identifiers.barcode_number,
        disabled: disabledForm,
      },
    },
    {
      id: "item_code",
      enabled: display.item_code,
      type: "input",
      section,
      params: {
        type: "autoExpand",
        name: "itemCode",
        label: labels.item_code,
        disabled: disabledForm,
      },
    },
    {
      id: "serial_number",
      enabled: display.serial_number,
      type: "serialNumberField",
      section,
      params: {
        type: "autoExpand",
        name: "serialNumber",
        label: l.identifiers.serial_number,
        disabled: disabledForm,
      },
    },
    {
      id: "id_cost_center",
      enabled: display.id_cost_center,
      type: "cost_center",
      section,
      params: {
        name: "costCenterId",
        label: labels.cost_center,
        disabled: disabledForm,
      },
    },
    {
      id: "receiving_report_number",
      enabled: !isItem && display.receiving_report_number && !isReceivingWizardDisabled,
      type: "inbound_summary",
      section,
      params: {
        asset,
        disabled: disabledForm,
      },
    },
    {
      id: "outbound_summary",
      enabled: !isItem && display.outbound_summary && !isReceivingWizardDisabled,
      type: "outbound_summary",
      section,
      params: {
        asset,
        disabled: disabledForm,
      },
    },
    {
      id: "created_at",
      enabled: !isItem && display.created_at,
      type: "text_field",
      section,
      params: {
        fullWidth: true,
        size: "small",
        label: l.identifiers.created_at,
        disabled: true,
        value: formatDate(asset.createdAt),
      },
    },
    {
      id: "updated_at",
      enabled: !isItem && display.updated_at,
      type: "text_field",
      section,
      params: {
        fullWidth: true,
        size: "small",
        label: l.identifiers.updated_at,
        disabled: true,
        value: formatDate(asset.updatedAt),
      },
    },
    {
      id: "last_in",
      enabled: !isItem && display.last_in,
      type: "monthDayYearPicker",
      section,
      params: {
        name: "lastIn",
        label: labels.last_in,
      },
    },
    {
      id: "last_out",
      enabled: !isItem && display.last_out,
      type: "text_field",
      section,
      params: {
        fullWidth: true,
        size: "small",
        label: labels.last_out,
        disabled: true,
        value: asset.lastOut ? formatDate(asset.lastOut) : "",
      },
    },
    {
      id: "inventory_number",
      enabled: display.inventory_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "inventoryNumber",
        label: l.identifiers.inventory_number,
        disabled: disabledForm,
      },
    },
    {
      id: "sku_number",
      enabled: display.sku_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "skuNumber",
        label: l.identifiers.sku_number,
        disabled: disabledForm,
      },
    },
    {
      id: "batch_number",
      enabled: display.batch_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "batchNumber",
        label: l.identifiers.batch_number,
        disabled: disabledForm,
      },
    },
    {
      id: "billing_type",
      enabled: display.billing_type,
      type: "select",
      section,
      params: {
        size: "small",
        name: "billingType",
        label: l.identifiers.billing_type,
        options: filteredBillingTypeOptions,
        disabled: disabledForm,
      },
    },
    {
      id: "osd_notes",
      enabled: !isItem && display.osd_notes,
      type: "text_field",
      section,
      params: {
        fullWidth: true,
        multiline: true,
        size: "small",
        label: labels.osd_notes,
        disabled: true,
        value: asset.osdNotes || "",
        maxRows: 4,
      },
    },
    {
      id: "general_notes",
      enabled: display.general_notes,
      type: "input",
      section,
      params: {
        multiline: true,
        name: "generalNotes",
        label: labels.general_notes,
        disabled: disabledForm,
      },
    },
    {
      id: "carrier_name",
      enabled: display.carrier_name,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "carrierName",
        label: labels.carrier_name,
        disabled: disabledForm,
      },
    },
    {
      id: "order_number",
      enabled: display.order_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "orderNumber",
        label: labels.order_number,
        disabled: disabledForm,
      },
    },
    {
      id: "bol_number",
      enabled: display.bol_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "bolNumber",
        label: labels.bol_number,
        disabled: disabledForm,
      },
    },
    {
      id: "tracking_number",
      enabled: display.tracking_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "trackingNumber",
        label: labels.tracking_number,
        disabled: disabledForm,
      },
    },
    {
      id: "truck_number",
      enabled: display.truck_number,
      type: "scannerInput",
      section,
      params: {
        type: "autoExpand",
        name: "truckNumber",
        label: labels.truck_number,
        disabled: disabledForm,
      },
    },
    {
      id: "attachments",
      enabled: !isItem && display.attachments,
      type: "attachments",
      section,
      params: {
        asset,
        disabled: disabledForm,
      },
    },
  ].filter(v => v.enabled);

  return fields;
}
